import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ChannelDetailsForm, ConsentBrandAssignment, ConsentConfigurationIdentifier } from '@twino/backoffice-api';
import { Observable } from 'rxjs';
import { ProductConfigService } from '@backoffice-monorepo/commons';

@Component({
  selector: 'backoffice-monorepo-consent-definition-brand',
  templateUrl: './consent-definition-brand.component.html',
  styleUrls: ['./consent-definition-brand.component.scss']
})
export class ConsentDefinitionBrandComponent implements OnInit, OnChanges {
  @Input() public parentForm: FormGroup;
  @Input() public data: Record<string, ConsentBrandAssignment> | never;

  productBrandsList$: Observable<string[]>;
  consentConfigurationIdentifier = ConsentConfigurationIdentifier;
  consentConfigurationIdentifierOptions = Object.keys(this.consentConfigurationIdentifier);
  selectedBrands: string[] = [];
  private selectedIdentifiers  = new Map<string, Set<string>>();

  constructor(
    private formBuilder: FormBuilder,
    private productConfigService: ProductConfigService
  ) {  }

  ngOnInit(): void {
    this.productBrandsList$ = this.productConfigService.listBrands();
    this.parentForm.addControl('brandAssignment', new FormArray([]));
  }

  ngOnChanges() {
    if (this.data) {
      this.setRows();
    }
  }

  setRows() {
    const formControl = this.parentForm.get('brandAssignment') as FormArray;
    Object.entries(this.data).forEach(
      (item) => {
        this.selectedBrands.push(item[0]);
        formControl.push(this.setFormGroupValues(item));
      }
    );
  }

  setFormGroupValues(item: [string, ConsentBrandAssignment]): FormGroup {
    const formGroupBrand = this.createForm(item[0]);
    formGroupBrand.get('brand').setValue(item[0]);
    this.disableBrand(item[0]);
    const formGroupDetails = formGroupBrand.get('channelDetails') as FormArray;
    Object.entries(item[1].channelDetails).forEach(
      (details) => {
        formGroupDetails.push(this.setFormGroupValuesIdentifier(item[0], {
          identifier: details[0],
          collect: details[1].collect,
          text: details[1].text
        }));
      })
    return formGroupBrand;
  }

  createForm(brand: string): FormGroup {
    return this.formBuilder.group({
      brand: brand,
      channelDetails: this.formBuilder.array([])
    });
  }

  get getFormControls() {
    return this.parentForm.get('brandAssignment') as FormArray;
  }

  addRow(brand: string) {
    if(brand !== '0') {
      const control = this.parentForm.get('brandAssignment') as FormArray;
      control.push(this.createForm(brand));
      this.disableBrand(brand);
    }
  }

  deleteRow(index: number) {
    const control = this.parentForm.get('brandAssignment') as FormArray;
    this.enableBrand(control.controls[index].value.brand);
    control.removeAt(index);
  }

  disableBrand(brand: string) {
    this.selectedBrands.push(brand);
  }

  enableBrand(brand: string) {
    this.selectedBrands.forEach((value, index) => {
      if (value === brand) this.selectedBrands.splice(index, 1);
    });
  }

  isDisabledBrand(brand: string) {
    return this.selectedBrands.includes(brand);
  }



  createFormIdentifier(identifier: string): FormGroup {
    return this.formBuilder.group({
      identifier: [identifier],
      text: ['', Validators.required],
      collect: [false]
    });
  }

  setFormGroupValuesIdentifier(brand: string, channelDetails: ChannelDetailsForm) {
    const formGroupReport = this.createFormIdentifier(channelDetails.identifier);
    this.disableIdentifier(brand, channelDetails.identifier);
    formGroupReport.setValue({
      identifier: channelDetails.identifier,
      text: channelDetails.text,
      collect: channelDetails.collect
    });
    return formGroupReport;
  }

  getFormControlsIdentifier(group: AbstractControl): AbstractControl[] {
    const formParent = group as FormGroup;
    const form = formParent.controls.channelDetails as FormArray;
    return form.controls;
  }

  addRowIdentifier(indexParent: number, identifier: string) {
    const parent = this.parentForm.get('brandAssignment') as FormArray;
    const control = parent.controls[indexParent].get('channelDetails') as FormArray;
    control.push(this.createFormIdentifier(identifier));
    this.disableIdentifier(parent.value[indexParent].brand, identifier);
  }

  deleteRowIdentifier(indexParent: number, index: number) {
    const parent = this.parentForm.get('brandAssignment') as FormArray;
    const control = parent.controls[indexParent].get('channelDetails') as FormArray;
    this.enableIdentifier(control.value[index].brand, control.value[index].identifier);
    control.removeAt(index);
  }

  disableIdentifier(brand: string, identifier: string) {
    if (!this.selectedIdentifiers.has(brand)) {
      this.selectedIdentifiers.set(brand, new Set());
    }
    this.selectedIdentifiers.get(brand)?.add(identifier);
  }

  enableIdentifier(brand: string, identifier: string) {
    const identifiers = this.selectedIdentifiers.get(brand);
    if (!identifiers) return false;

    const deleted = identifiers.delete(identifier);
    if (identifiers.size === 0) {
      this.selectedIdentifiers.delete(brand);
    }
    return deleted;
  }

  isDisabledIdentifier(indexParent: number, identifier: string): boolean {
    const parent = this.parentForm.get('brandAssignment') as FormArray;
    const brand = parent.value[indexParent].brand;
    return this.selectedIdentifiers.has(brand) && this.selectedIdentifiers.get(brand)?.has(identifier);
  }

  trackBy(_: number, control: AbstractControl): AbstractControl {
    return control;
  }
}
